import { useEffect, useRef, useState } from 'react'
import { useReducedMotion } from '@/lib/useReducedMotion'

// PoC Phase 2.1: snake line that alternates left↔right between sections.
//
// Anchoring (#1): vertical runs are pinned to the page's content rail
// (max-w-7xl with md:px-8 padding → 1216px inner), with the line centered
// LINE_OFFSET (20px = 2.5 dot diameters) outside the content. Stays close
// to content on wide viewports instead of drifting to the viewport edges.
//
// Spacing (#2): 2.5 dot-diameter clearance enforced both horizontally
// (line center ≥ 20px from content edge) and vertically (bend lies in
// the inter-section gap, ≥ 20px from either neighbour's content edge).
//
// Bend placement (#3): bend_y sits at the visual midpoint of each
// inter-section gap (prev.bottom → curr.top), not at the midpoint of
// section centers. Combined with a gap-region length mapping, the snake
// unfolds inside the empty space between sections instead of cutting
// through content.

type Node = {
  id: string
  y: number       // section vertical center (dot position)
  top: number     // section.offsetTop
  bottom: number  // section.offsetTop + section.offsetHeight
  x: number       // LEFT_X or RIGHT_X
  cumLen: number  // cumulative path length up to this node
  // Cached per-segment values for the scroll-time length mapping
  topVertLen: number    // prev.bottom - prev.y, length drawn 1:1 with Y on entering this segment
  snakeLen: number      // arc + horizontal + arc (or simply `gap` for same-side)
  gap: number           // curr.top - prev.bottom (≥ 0; 0 for the first node)
}

const NODE_RADIUS = 4
const STROKE_WIDTH = 1.5
const ARC_RADIUS = 6      // matches rounded-md
const LINE_OFFSET = 28    // 3 visible dot-diameters (edge-to-edge) from content edge
const CONTENT_INNER = 1216 // max-w-7xl (1280) − 2 × md:px-8 (32) — inner text rail
const SECTION_PAD = 32     // md:px-8
const HEAD_OFFSET = 0.5
const FADE_FLOOR = 0.66

export function ScrollProgressLine() {
  const svgRef = useRef<SVGSVGElement>(null)
  const reduced = useReducedMotion()
  const [nodes, setNodes] = useState<Node[]>([])
  const [pathD, setPathD] = useState('')
  const [totalLen, setTotalLen] = useState(0)
  const [svgHeight, setSvgHeight] = useState(0)
  const brandPathRef = useRef<SVGPathElement>(null)

  // ── 1. Measure sections + build the snake path ─────────────────────────
  useEffect(() => {
    const svg = svgRef.current
    if (!svg) return
    const wrapper = svg.parentElement
    if (!wrapper) return

    let raf: number | null = null

    const measure = () => {
      raf = null
      const sections = Array.from(wrapper.querySelectorAll<HTMLElement>(':scope > section'))
      if (sections.length === 0) return

      const w = wrapper.offsetWidth
      // Anchor to content rail. On viewports wider than the rail, line
      // centers sit LINE_OFFSET outside content. On narrow viewports the
      // rail collapses to the section padding edge.
      const innerRailLeft = Math.max(SECTION_PAD, (w - CONTENT_INNER) / 2)
      const innerRailRight = w - innerRailLeft
      const leftX = Math.max(NODE_RADIUS, innerRailLeft - LINE_OFFSET)
      const rightX = Math.min(w - NODE_RADIUS, innerRailRight + LINE_OFFSET)

      const measured: Node[] = []
      let cumLen = 0
      for (let i = 0; i < sections.length; i++) {
        const el = sections[i]
        const x = i % 2 === 0 ? leftX : rightX
        const top = el.offsetTop
        const bottom = top + el.offsetHeight
        const y = top + el.offsetHeight / 2

        let topVertLen = 0
        let snakeLen = 0
        let gap = 0
        if (i > 0) {
          const prev = measured[i - 1]
          gap = Math.max(0, top - prev.bottom)
          topVertLen = Math.max(0, prev.bottom - prev.y)
          const isSideSwap = prev.x !== x
          // For side-swaps: top vert (1:1 with Y down to prev.bottom),
          // then the snake region — running through the inter-section gap —
          // contains both the residual vertical extensions (V1 past prev.bottom,
          // V2 before curr.top), the two quarter-arcs, and the horizontal
          // traverse. Its total path length equals: gap + |Δx| + (π−4)·r.
          snakeLen = isSideSwap
            ? gap + Math.abs(x - prev.x) + (Math.PI - 4) * ARC_RADIUS
            : gap
          const bottomVertLen = Math.max(0, y - top)
          cumLen += topVertLen + snakeLen + bottomVertLen
        }

        measured.push({
          id: el.id || `s${i}`,
          y,
          top,
          bottom,
          x,
          cumLen,
          topVertLen,
          snakeLen,
          gap,
        })
      }

      // Build the SVG d-string. bend_y is the midpoint of the inter-section
      // gap, so the snake threads through empty space rather than cutting
      // through section content.
      let d = `M ${measured[0].x} ${measured[0].y}`
      for (let i = 1; i < measured.length; i++) {
        const prev = measured[i - 1]
        const curr = measured[i]
        if (prev.x === curr.x) {
          d += ` L ${curr.x} ${curr.y}`
          continue
        }
        const bendY = (prev.bottom + curr.top) / 2
        const goingRight = curr.x > prev.x
        d += ` L ${prev.x} ${bendY - ARC_RADIUS}`
        if (goingRight) {
          d += ` A ${ARC_RADIUS} ${ARC_RADIUS} 0 0 0 ${prev.x + ARC_RADIUS} ${bendY}`
          d += ` L ${curr.x - ARC_RADIUS} ${bendY}`
          d += ` A ${ARC_RADIUS} ${ARC_RADIUS} 0 0 1 ${curr.x} ${bendY + ARC_RADIUS}`
        } else {
          d += ` A ${ARC_RADIUS} ${ARC_RADIUS} 0 0 1 ${prev.x - ARC_RADIUS} ${bendY}`
          d += ` L ${curr.x + ARC_RADIUS} ${bendY}`
          d += ` A ${ARC_RADIUS} ${ARC_RADIUS} 0 0 0 ${curr.x} ${bendY + ARC_RADIUS}`
        }
        d += ` L ${curr.x} ${curr.y}`
      }

      setNodes(measured)
      setPathD(d)
      setTotalLen(cumLen)
      setSvgHeight(wrapper.offsetHeight)
    }

    const measureDeferred = () => {
      if (raf !== null) return
      raf = requestAnimationFrame(measure)
    }

    measureDeferred()
    const ro = new ResizeObserver(measureDeferred)
    ro.observe(wrapper)
    window.addEventListener('resize', measureDeferred)

    return () => {
      ro.disconnect()
      window.removeEventListener('resize', measureDeferred)
      if (raf !== null) cancelAnimationFrame(raf)
    }
  }, [])

  // ── 2. Scroll handler — imperative DOM mutation, no re-renders ─────────
  useEffect(() => {
    const svg = svgRef.current
    if (!svg || nodes.length < 2) return
    const wrapper = svg.parentElement
    if (!wrapper) return

    const path = svg.querySelector<SVGPathElement>('path')
    const circles = Array.from(svg.querySelectorAll<SVGCircleElement>('circle'))
    if (!path) return

    if (reduced) {
      path.setAttribute('stroke-dashoffset', '0')
      circles.forEach((c) => {
        c.style.fillOpacity = '1'
        c.style.strokeOpacity = '1'
      })
      return
    }

    let raf: number | null = null

    const update = () => {
      raf = null
      const sy = window.scrollY
      const vp = window.innerHeight
      const wrapperPageTop = wrapper.getBoundingClientRect().top + sy
      const drawHeadY = sy + vp * HEAD_OFFSET - wrapperPageTop

      // Map page-Y → drawn path length using a *gap-aware* mapping per
      // segment. The vertical runs draw 1:1 with Y (inside each section),
      // and the snake region's path length unfolds linearly across the
      // inter-section gap — so the bend visually traces the empty space
      // between sections instead of cutting through content.
      let drawnLen: number
      const first = nodes[0]
      const last = nodes[nodes.length - 1]
      if (drawHeadY <= first.y) {
        drawnLen = 0
      } else if (drawHeadY >= last.y) {
        drawnLen = totalLen
      } else {
        drawnLen = 0
        for (let i = 1; i < nodes.length; i++) {
          const prev = nodes[i - 1]
          const curr = nodes[i]
          if (drawHeadY >= prev.y && drawHeadY <= curr.y) {
            if (drawHeadY <= prev.bottom) {
              // Top vertical: 1:1 with Y
              drawnLen = prev.cumLen + (drawHeadY - prev.y)
            } else if (drawHeadY >= curr.top) {
              // Bottom vertical: 1:1 with Y, past the snake region
              drawnLen =
                prev.cumLen + curr.topVertLen + curr.snakeLen + (drawHeadY - curr.top)
            } else {
              // Inside the gap → snake region. Linearly distribute the
              // snake's path length across the gap.
              const t = curr.gap > 0 ? (drawHeadY - prev.bottom) / curr.gap : 1
              drawnLen = prev.cumLen + curr.topVertLen + t * curr.snakeLen
            }
            break
          }
        }
      }
      path.setAttribute('stroke-dashoffset', String(Math.max(0, totalLen - drawnLen)))

      const brandPath = brandPathRef.current
      if (brandPath) {
        brandPath.setAttribute('stroke-dashoffset', String(80 - drawnLen))
      }

      // Find the most-recently-passed node (highest index where drawnLen >= cumLen)
      let leadingIdx = -1
      for (let i = 0; i < nodes.length; i++) {
        if (drawnLen >= nodes[i].cumLen) leadingIdx = i
        else break
      }

      circles.forEach((c, i) => {
        const nodeY = nodes[i].y
        const vpY = nodeY + wrapperPageTop - sy
        let strokeOp: number
        if (vpY <= vp * FADE_FLOOR) strokeOp = 1
        else if (vpY <= vp) strokeOp = 1 - (vpY - vp * FADE_FLOOR) / (vp * (1 - FADE_FLOOR))
        else strokeOp = 0
        c.style.strokeOpacity = String(Math.max(0, Math.min(1, strokeOp)))
        c.style.fillOpacity = drawnLen >= nodes[i].cumLen ? '1' : '0'

        // Brand fill + stroke on the most-recently-passed node
        if (i === leadingIdx) {
          c.style.fill = 'var(--brand-accent)'
          c.style.stroke = 'var(--brand-accent)'
        } else {
          c.style.fill = 'currentColor'
          c.style.stroke = 'currentColor'
        }
      })
    }

    const onScroll = () => {
      if (raf !== null) return
      raf = requestAnimationFrame(update)
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    update()

    return () => {
      window.removeEventListener('scroll', onScroll)
      if (raf !== null) cancelAnimationFrame(raf)
    }
  }, [nodes, totalLen, reduced])

  const ready = nodes.length >= 2

  return (
    <svg
      ref={svgRef}
      aria-hidden="true"
      className="absolute inset-0 pointer-events-none text-mono-800 dark:text-mono-100 hidden md:block"
      style={{ width: '100%', height: svgHeight || '100%', zIndex: -1 }}
    >
      {ready && (
        <>
          <path
            d={pathD}
            stroke="currentColor"
            strokeOpacity={0.22}
            strokeWidth={STROKE_WIDTH}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
            strokeDasharray={totalLen}
            strokeDashoffset={totalLen}
            style={{ transition: 'stroke-dashoffset 280ms cubic-bezier(0.22, 1, 0.36, 1)' }}
          />
          <path
            ref={brandPathRef}
            d={pathD}
            stroke="var(--brand-accent)"
            strokeWidth={STROKE_WIDTH}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
            strokeDasharray={`80 ${totalLen}`}
            strokeDashoffset={80}
            style={{ transition: 'stroke-dashoffset 280ms cubic-bezier(0.22, 1, 0.36, 1)' }}
          />
          {nodes.map((n) => (
            <circle
              key={n.id}
              cx={n.x}
              cy={n.y}
              r={NODE_RADIUS}
              fill="currentColor"
              fillOpacity={0}
              stroke="currentColor"
              strokeOpacity={0}
              strokeWidth={STROKE_WIDTH}
              style={{
                transition:
                  'fill 280ms ease, stroke 280ms ease, fill-opacity 280ms cubic-bezier(0.2, 0.8, 0.4, 1), stroke-opacity 240ms linear',
              }}
            />
          ))}
        </>
      )}
    </svg>
  )
}
